import React from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface ListingTypeBadgeProps {
  listingType: string;
  className?: string;
  itemType?: 'part' | 'tool' | 'equipment';
  specificityType?: 'helicopter' | 'engine' | 'general';
}

// Helper function to generate dynamic labels for parts listings
const getPartsLabel = (
  listingType: string,
  itemType?: string,
  specificityType?: string
): string => {
  const item = itemType === 'tool' ? 'Tool' : itemType === 'equipment' ? 'Equipment' : 'Part';
  const prefix = specificityType === 'helicopter' ? 'Aircraft ' : specificityType === 'engine' ? 'Engine ' : '';

  if (listingType.includes('sell')) return `${prefix}${item} For Sale`;
  if (listingType === 'parts_rent') return `${prefix}${item} For Rent`;
  if (listingType.includes('buy')) return `Seeking ${prefix}${item}`;
  if (listingType === 'parts_rent_request') return `Seeking ${prefix}${item} Rental`;
  return `${prefix}${item}`;
};

const ListingTypeBadge: React.FC<ListingTypeBadgeProps> = ({ 
  listingType, 
  className,
  itemType,
  specificityType
}) => {
  const getBadgeConfig = (type: string) => {
    switch (type) {
      case 'sale':
        return {
          label: 'For Sale',
          className: 'bg-gradient-to-r from-emerald-600 via-green-500 to-emerald-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'dry_lease':
        return {
          label: 'For Dry Lease',
          className: 'bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'wet_lease':
        return {
          label: 'For Wet Lease',
          className: 'bg-gradient-to-r from-purple-600 via-violet-500 to-purple-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'buy_sale':
        return {
          label: 'Looking to Buy',
          className: 'bg-gradient-to-r from-rose-600 via-pink-500 to-rose-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'buy_dry_lease':
        return {
          label: 'Seeking Dry Lease',
          className: 'bg-gradient-to-r from-yellow-600 via-amber-500 to-yellow-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'buy_wet_lease':
        return {
          label: 'Seeking Wet Lease',
          className: 'bg-gradient-to-r from-indigo-600 via-blue-500 to-indigo-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'engine_sell':
        return {
          label: 'Engine For Sale',
          className: 'bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'engine_rent':
        return {
          label: 'Engine For Rent',
          className: 'bg-gradient-to-r from-teal-600 via-emerald-500 to-teal-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'engine_buy':
        return {
          label: 'Seeking Engine',
          className: 'bg-gradient-to-r from-rose-600 via-pink-500 to-rose-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'engine_rent_request':
        return {
          label: 'Seeking Engine Rental',
          className: 'bg-gradient-to-r from-orange-600 via-amber-500 to-orange-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      // Parts & Tools - For Sale types
      case 'parts_sell_aircraft':
        return {
          label: getPartsLabel(type, itemType, specificityType),
          className: 'bg-gradient-to-r from-slate-600 via-zinc-500 to-slate-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'parts_sell_engine':
        return {
          label: getPartsLabel(type, itemType, specificityType),
          className: 'bg-gradient-to-r from-sky-600 via-blue-500 to-sky-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'parts_sell_general':
        return {
          label: getPartsLabel(type, itemType, specificityType),
          className: 'bg-gradient-to-r from-slate-600 via-gray-500 to-slate-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      // Parts & Tools - For Rent
      case 'parts_rent':
        return {
          label: getPartsLabel(type, itemType, specificityType),
          className: 'bg-gradient-to-r from-cyan-600 via-teal-500 to-cyan-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      // Parts & Tools - Seeking to Buy
      case 'parts_buy_aircraft':
        return {
          label: getPartsLabel(type, itemType, specificityType),
          className: 'bg-gradient-to-r from-fuchsia-600 via-pink-500 to-fuchsia-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'parts_buy_engine':
        return {
          label: getPartsLabel(type, itemType, specificityType),
          className: 'bg-gradient-to-r from-pink-600 via-rose-500 to-pink-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'parts_buy_general':
        return {
          label: getPartsLabel(type, itemType, specificityType),
          className: 'bg-gradient-to-r from-rose-600 via-red-400 to-rose-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      // Parts & Tools - Seeking Rental
      case 'parts_rent_request':
        return {
          label: getPartsLabel(type, itemType, specificityType),
          className: 'bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      // Job listing types
      case 'job_post':
        return {
          label: 'Job Posting',
          className: 'bg-gradient-to-r from-emerald-600 via-green-500 to-emerald-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'job_contract':
        return {
          label: 'Contract Work',
          className: 'bg-gradient-to-r from-purple-600 via-violet-500 to-purple-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'job_profile':
        return {
          label: 'Professional',
          className: 'bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'job_seeking':
        return {
          label: 'Looking for Work',
          className: 'bg-gradient-to-r from-blue-600 via-sky-500 to-blue-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      // Service listing types
      case 'service_offer':
        return {
          label: 'Service Provider',
          className: 'bg-gradient-to-r from-emerald-600 via-green-500 to-emerald-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'capability_listing':
        return {
          label: 'Capability',
          className: 'bg-gradient-to-r from-violet-600 via-purple-500 to-violet-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'service_request':
        return {
          label: 'Service Request',
          className: 'bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      case 'rfq_request':
        return {
          label: 'RFQ',
          className: 'bg-gradient-to-r from-orange-600 via-red-500 to-orange-600 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
      default:
        return {
          label: 'Listing',
          className: 'bg-gradient-to-r from-gray-700 via-gray-600 to-gray-700 text-white border-0 font-bold shadow-xl backdrop-blur-sm',
        };
    }
  };

  const config = getBadgeConfig(listingType);

  return (
    <Badge className={cn(config.className, className)}>
      {config.label}
    </Badge>
  );
};

export default ListingTypeBadge;
